import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Search, X } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth, userCan } from '@/components/auth/AuthContext'
import { PAGES, useGlobalSearch, type FlatItem } from '@/hooks/useGlobalSearch'
import { useModalA11y } from '@/hooks/useModalA11y'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { QueueIcon, queueIconColor, type QueueIconColor } from '@/components/QueueIcon'
import { WorkspaceEmptyState } from '@/components/feedback/WorkspaceEmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { FilterPills } from '@/components/FilterPills'
import { FilterDisclosure } from '@/components/FilterDisclosure'
import { ConversationAvatar } from '@/pages/authenticated/conversations/ConversationAvatar'
import { HomeClayIcon } from '@/pages/authenticated/home/HomeClayIcon'
import { QUICK_ACTIONS, matchesAction, matchesActionStrongly, type QuickAction } from './actions'
import { ActionMark, ActionPreview, PageMark, SearchPreview, recordDestination, recordOpenLabel } from './SearchPreview'
import { useCommandPalette, type PaletteSource } from './useCommandPalette'
import k from '@/styles/list-shared.module.css'
import s from './GlobalSearchDialog.module.css'

type Category = 'all' | 'actions' | FlatItem['type']
const CATEGORIES: Record<Category, string> = { all: 'All', actions: 'Actions', customer: 'Customers', job: 'Jobs', invoice: 'Invoices', page: 'Pages' }

type Tone = QueueIconColor | 'navy'
type Row =
  | { key: string; kind: 'record'; item: FlatItem; tone: Tone }
  | { key: string; kind: 'action'; action: QuickAction; tone: Tone }
interface Group { key: string; label: string; rows: Array<{ row: Row; index: number }> }

/** A record row's 44px mark (64px in the identity): seeded clay for customers
 *  and jobs, the invoice entity mark, the destination mark for pages. */
export function SearchMark({ item, large = false }: { item: FlatItem; large?: boolean }) {
  const className = large ? s.largeMark : s.mark
  if (item.type === 'customer') return <ConversationAvatar seed={item.id} className={className} />
  if (item.type === 'job') return <HomeClayIcon meaning="job" seed={item.id} className={className} />
  if (item.type === 'page') return <PageMark pathname={item.href.split(/[?#]/)[0]} large={large} />
  return <QueueIcon meaning={item.type} color={queueIconColor(item.id)} className={className} />
}

function Highlight({ text, query }: { text: string; query: string }) {
  const term = query.trim()
  const index = term ? text.toLocaleLowerCase().indexOf(term.toLocaleLowerCase()) : -1
  return index < 0 ? <>{text}</> : <>{text.slice(0, index)}<mark>{text.slice(index, index + term.length)}</mark>{text.slice(index + term.length)}</>
}

const RISE = 'cubic-bezier(.16,1,.3,1)'
const STANDARD = 'cubic-bezier(.4,0,.2,1)'

/** The transform that lays the panel over the launching field. */
function fieldTransform(rect: DOMRect, target: DOMRect): string {
  return `translate(${rect.x - target.x}px, ${rect.y - target.y}px) scale(${rect.width / target.width}, ${rect.height / target.height})`
}

/**
 * Transform-only glide out of the launching field (340ms, the design system's
 * curve) and back into it on close (240ms, the next duration down). Only the
 * frame stretches: the content fades in once the frame is within about 3% of
 * its shape (120ms in) and fades out before it shrinks, so text and marks are
 * never squashed. A shortcut (0×0 rect) or reduced motion opens and closes at
 * once. Layout is already final, so keyboard focus never waits for the motion.
 */
function useSearchGlide(
  panelRef: RefObject<HTMLDivElement | null>,
  scrimRef: RefObject<HTMLButtonElement | null>,
  rect: DOMRect,
  open: boolean,
  onExited: () => void,
) {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  // Decided once per open, so the exit mirrors the entrance.
  const glide = useRef<boolean | null>(null)
  glide.current ??= !reduced && rect.width > 0 && rect.height > 0
  const layout = useRef<DOMRect | null>(null)
  const exited = useRef(onExited)
  exited.current = onExited
  const closing = useRef(!open)
  closing.current = !open

  useLayoutEffect(() => {
    const panel = panelRef.current
    const scrim = scrimRef.current
    if (!open || !glide.current || !panel || !scrim) return
    const target = panel.getBoundingClientRect()
    layout.current = target
    const animations = [
      panel.animate([{ opacity: 0.35, transform: fieldTransform(rect, target) }, { opacity: 1, transform: 'none' }], { duration: 340, easing: RISE }),
      ...Array.from(panel.children, child => child.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, delay: 120, easing: STANDARD, fill: 'backwards' })),
      scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: STANDARD }),
    ]
    return () => {
      for (const animation of animations) {
        // A close mid-entrance leaves from wherever the entrance got to.
        if (closing.current && animation.playState === 'running') animation.commitStyles()
        animation.cancel()
      }
    }
  }, [panelRef, scrimRef, rect, open])

  useLayoutEffect(() => {
    const panel = panelRef.current
    const scrim = scrimRef.current
    if (open) return
    if (!glide.current || !panel || !scrim || !layout.current) {
      exited.current()
      return
    }
    const frame = panel.animate([{ opacity: 0, transform: fieldTransform(rect, layout.current) }], { duration: 180, delay: 60, easing: STANDARD, fill: 'forwards' })
    const animations = [
      frame,
      ...Array.from(panel.children, child => child.animate([{ opacity: 0 }], { duration: 60, easing: STANDARD, fill: 'forwards' })),
      scrim.animate([{ opacity: 0 }], { duration: 240, easing: STANDARD, fill: 'forwards' }),
    ]
    frame.finished.then(() => exited.current(), () => {})
    return () => animations.forEach(animation => animation.cancel())
  }, [panelRef, scrimRef, rect, open])
}

/** Navigate rows borrow the page catalog's description so the two never drift. */
function describeAction(action: QuickAction): string {
  return action.description ?? PAGES.find(page => page.href === action.to)?.sub ?? ''
}
function recordRow(item: FlatItem): Row {
  return { key: `${item.type}:${item.id}`, kind: 'record', item, tone: item.type === 'page' ? 'navy' : queueIconColor(item.id) }
}
function actionRow(action: QuickAction): Row {
  return { key: `action:${action.key}`, kind: 'action', action, tone: action.mark.kind === 'entity' ? action.mark.color : 'navy' }
}

/**
 * The one command dialog (Corpus header-search-v1): ⌘K and the sidebar's
 * Quick actions pill open it on the Actions category; `/`, the header field,
 * and the sidebar's search button open it on All. Quick actions are a group
 * beside the matched pages / customers / jobs / invoices; the right column
 * previews whichever row is highlighted.
 *
 * Keyboard: ↑/↓ move the highlight, Enter opens a record's details or runs an
 * action, Escape returns from details then closes, Tab stays inside (focus
 * trap + scroll lock from useModalA11y, DES-08), closing restores focus.
 */
export function GlobalSearchDialog({ source, open, onExited }: {
  source: PaletteSource
  /** False while the dialog plays its exit: inert, focus already returned. */
  open: boolean
  onExited: (source: PaletteSource) => void
}) {
  const { mode, close, lastQuery, requestHelp } = useCommandPalette()
  const navigate = useNavigate()
  const { signOut, user } = useAuth()
  // Text typed into the header field opens with it; otherwise record search
  // reopens on the query that survived closing (design system), and ⌘K's
  // Quick actions open empty without forgetting it.
  const search = useGlobalSearch({ suggestPages: true, initialQuery: source.query ?? (mode === 'search' ? lastQuery.current : '') })
  const [category, setCategory] = useState<Category>(() => (mode === 'actions' ? 'actions' : 'all'))
  const [detail, setDetail] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const scrim = useRef<HTMLButtonElement>(null)
  const id = useId()
  const escape = useRef<() => void>(close)
  escape.current = () => { if (detail) { setDetail(false); input.current?.focus() } else close() }
  // Keep the modal's subscription stable while the query/preview changes.
  const onEscape = useCallback(() => escape.current(), [])
  const dialog = useModalA11y<HTMLDivElement>(open, onEscape)
  useSearchGlide(dialog, scrim, source.rect, open, () => onExited(source))

  const query = search.query.trim()
  // Draft creation is open to every member; approve and send remains the
  // server-enforced money gate. Reports retain their separate read permission.
  const actions = useMemo(
    () => QUICK_ACTIONS.filter(action => (!action.requiresReportsPerm || userCan(user, 'canViewReports')) && matchesAction(action, query)),
    [user, query],
  )
  const counts = useMemo(() => {
    const result: Record<Category, number> = { all: actions.length + search.allItems.length, actions: actions.length, customer: 0, job: 0, invoice: 0, page: 0 }
    for (const item of search.allItems) result[item.type]++
    return result
  }, [actions, search.allItems])
  const categoryTabs = useMemo(() => (Object.keys(CATEGORIES) as Category[]).map(value => ({
    key: value, label: CATEGORIES[value], count: counts[value],
  })), [counts])
  const { groups, rows } = useMemo(() => {
    const actionGroup = { key: 'actions', label: 'Quick actions', rows: actions.map(actionRow) }
    const recordGroup = {
      key: 'records',
      label: query ? 'Matching records' : 'Explore your workspace',
      rows: search.allItems.filter(item => category === 'all' || item.type === category).map(recordRow),
    }
    // In All, actions lead only while the query reads like one of their labels.
    const actionsFirst = !query || actions.some(action => matchesActionStrongly(action, query))
    const ordered = category === 'actions' ? [actionGroup] : category !== 'all' ? [recordGroup] : actionsFirst ? [actionGroup, recordGroup] : [recordGroup, actionGroup]
    let index = 0
    const groups: Group[] = ordered.filter(group => group.rows.length).map(group => ({ ...group, rows: group.rows.map(row => ({ row, index: index++ })) }))
    return { groups, rows: groups.flatMap(group => group.rows.map(entry => entry.row)) }
  }, [actions, search.allItems, category, query])
  const active = Math.min(search.selectedIndex, Math.max(0, rows.length - 1))
  const selected = rows[active] ?? null
  const showDetail = detail && selected?.kind === 'record'
  const destination = selected?.kind === 'record' ? recordDestination(selected.item) : null

  useEffect(() => {
    if (!open) return
    setCategory(mode === 'actions' ? 'actions' : 'all')
    setDetail(false)
    input.current?.focus()
    // A query that survived closing is selected, so typing replaces it; text
    // typed into the header field keeps its caret at the end.
    if (source.query === undefined) input.current?.select()
    else lastQuery.current = source.query
    return () => { if (source.opener?.isConnected) source.opener.focus() }
  }, [open, mode, source, lastQuery])
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active, category, search.query])

  function runAction(action: QuickAction) {
    // Search records starts a fresh record search: the text that found the
    // action is not a record query (it would only find the action again).
    if (action.run === 'search') { reset(); return }
    close()
    if (action.run === 'help') requestHelp()
    else if (action.run === 'signOut') void signOut()
    else if (action.to) navigate(action.to)
  }
  function activate(row: Row) { if (row.kind === 'action') runAction(row.action); else setDetail(true) }
  // Only a record search is remembered: text typed to find a quick action is
  // not a record query, so ⌘K never overwrites what / reopens on.
  function change(event: ChangeEvent<HTMLInputElement>) { search.setQuery(event.target.value); if (category !== 'actions') lastQuery.current = event.target.value; setDetail(false) }
  function filter(value: Category) { setCategory(value); search.setSelectedIndex(0); setDetail(false) }
  function changeCategory(event: ChangeEvent<HTMLSelectElement>) { filter(event.target.value as Category) }
  function clearCategory() { filter('all') }
  function highlight(event: PointerEvent<HTMLButtonElement>) { search.setSelectedIndex(Number(event.currentTarget.dataset.index)) }
  function select(event: MouseEvent<HTMLButtonElement>) {
    const index = Number(event.currentTarget.dataset.index)
    search.setSelectedIndex(index)
    const row = rows[index]
    if (row) activate(row)
  }
  function back() { setDetail(false); input.current?.focus() }
  function reset() { search.reset(); lastQuery.current = ''; setCategory('all'); setDetail(false); input.current?.focus() }
  function retry() { search.retry(); input.current?.focus() }
  function keepFocus(event: MouseEvent<HTMLButtonElement>) { event.preventDefault() }
  function key(event: KeyboardEvent<HTMLInputElement>) {
    if (event.nativeEvent.isComposing) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); setDetail(false)
      if (rows.length) search.setSelectedIndex((active + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length)
    } else if (event.key === 'Enter' && selected) { event.preventDefault(); activate(selected) }
  }

  const count = rows.length
  const countLabel = category === 'actions'
    ? `${count} quick ${count === 1 ? 'action' : 'actions'}`
    : `${count} ${count === 1 ? 'result' : 'results'} ${category === 'all' ? 'across your workspace' : `in ${CATEGORIES[category]}`}`
  const status = search.isLoading
    ? <p role="status" className={s.status}>Searching records…</p>
    : search.isError ? <ErrorState compact message="Records couldn’t be loaded. Try again." onRetry={retry} /> : null

  return createPortal(
    <div className={open ? s.overlay : `${s.overlay} ${s.closing}`} {...(open ? {} : { inert: '' as const })}>
      <button ref={scrim} type="button" className={s.backdrop} tabIndex={-1} aria-label="Close" onClick={close} />
      <div ref={dialog} className={s.dialog} role="dialog" aria-modal="true" aria-label="Search and quick actions" tabIndex={-1} data-detail={showDetail}>
        <div className={s.searchLine}>
          <div className={s.searchField}>
            <Search size={18} className={k.searchIcon} aria-hidden="true" />
            <input ref={input} className={k.searchInput} type="text" inputMode="search" role="combobox" aria-label="Search records, pages, and quick actions" aria-autocomplete="list" aria-expanded={!showDetail} aria-controls={!showDetail ? id : undefined} aria-activedescendant={!showDetail && selected ? `${id}-${active}` : undefined} autoComplete="off" placeholder="Search anything…" value={search.query} onChange={change} onKeyDown={key} />
            {search.query && <button type="button" className={`${k.searchClear} ${s.searchClear}`} onClick={reset} aria-label="Clear search and filters"><X size={18} /><span className={s.tip} role="tooltip">Clear search and filters</span></button>}
          </div>
          <button type="button" className={s.iconButton} onClick={close} aria-label="Close"><kbd className={s.key}>esc</kbd><span className={s.tip} role="tooltip">Close</span></button>
        </div>
        <div className={s.categories}>
          <FilterDisclosure
            label="Show search filters"
            activeCount={Number(category !== 'all')}
            lenses={<FilterPills variant="workspace" className={s.categoryTabs} ariaLabel="Search categories" tabs={categoryTabs} value={category} onChange={filter} />}
          >
            <label className={s.categoryField} htmlFor={`${id}-category`}>
              Search in
              <select id={`${id}-category`} className={k.select} value={category} onChange={changeCategory}>
                {categoryTabs.map(tab => <option key={tab.key} value={tab.key}>{tab.label}</option>)}
              </select>
            </label>
            {category !== 'all' && <button type="button" className={s.secondaryAction} onClick={clearCategory}>Clear filter</button>}
          </FilterDisclosure>
        </div>
        <div className={s.body}>
          {rows.length ? <>
            <div className={s.results}>
              {status}
              <div ref={list} id={id} role="listbox" aria-label="Results" aria-busy={search.isLoading}>
                {groups.map(group => (
                  <div key={group.key} role="group" aria-labelledby={`${id}-${group.key}`} className={s.group}>
                    <p id={`${id}-${group.key}`} className={s.resultsLabel}>{group.label}</p>
                    {group.rows.map(({ row, index }) => {
                      const title = row.kind === 'action' ? row.action.label : row.item.label
                      const meta = row.kind === 'action' ? describeAction(row.action) : row.item.sub
                      return (
                        <button type="button" role="option" tabIndex={-1} id={`${id}-${index}`} key={row.key} data-index={index} data-entity-tone={row.tone} aria-selected={index === active} className={s.result} onMouseDown={keepFocus} onPointerMove={highlight} onClick={select}>
                          {row.kind === 'action' ? <ActionMark mark={row.action.mark} /> : <SearchMark item={row.item} />}
                          <span className={s.resultText}><strong><Highlight text={title} query={search.query} /></strong><small><Highlight text={meta} query={search.query} /></small></span>
                          <span className={s.resultArrow} aria-hidden="true">↵</span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
            {selected && <section className={s.preview} aria-label="Preview" data-entity-tone={selected.tone}>
              <div className={s.previewScroll}>
                {showDetail && <button type="button" className={s.back} onClick={back}><ArrowLeft size={15} aria-hidden="true" />Back to results</button>}
                {selected.kind === 'action' ? <ActionPreview action={selected.action} description={describeAction(selected.action)} /> : <SearchPreview item={selected.item} />}
              </div>
              <div className={s.previewActions}>
                {selected.kind === 'action'
                  ? <button type="button" className={`${k.btn} ${k.btnAlt} ${s.primaryAction}`} onClick={() => runAction(selected.action)}>{selected.action.verb} <span aria-hidden="true">↵</span></button>
                  : <>
                    <Link to={selected.item.href} onClick={close} className={`${k.btn} ${k.btnAlt} ${s.primaryAction}`}>{recordOpenLabel(selected.item)} <span aria-hidden="true">↗</span></Link>
                    {destination && <Link to={destination.href} onClick={close} className={s.destination}>{destination.label} <span aria-hidden="true">→</span></Link>}
                  </>}
              </div>
            </section>}
          </> : <div className={s.empty}>
            {status ?? <WorkspaceEmptyState kind="search" compact title="No matches yet" body={query ? 'Try another name, invoice number, or a shorter phrase.' : 'Type a name or record number to search this category.'} action={<button type="button" className={s.secondaryAction} onClick={reset}>Clear search and filters</button>} />}
          </div>}
        </div>
        <footer className={s.footer}>
          <span role="status" aria-live="polite">{countLabel}</span>
          <span className={s.footerHelp} aria-hidden="true"><span><kbd className={s.key}>↑↓</kbd> Select</span><span><kbd className={s.key}>↵</kbd> Open</span><span><kbd className={s.key}>esc</kbd> Close</span></span>
        </footer>
      </div>
    </div>, document.body,
  )
}
